import { useEffect, useMemo, useState } from "react";

import { useOverlayDismiss } from "../../lib/useOverlayDismiss";
import { ApiError, apiClient } from "../../api/client";
import type { LedgerEntry, Season } from "../../api/types";
import { formatDate, formatDateTime, formatNumber } from "../../lib/format";
import { fetchAllLedger } from "../../lib/ledger";

interface State {
  status: "loading" | "ready" | "error";
  seasons: Season[];
  errorMessage: string | null;
}

const initial: State = {
  status: "loading",
  seasons: [],
  errorMessage: null
};

export function Seasons() {
  const [state, setState] = useState<State>(initial);
  const [creating, setCreating] = useState(false);
  const [createName, setCreateName] = useState("");
  const [createError, setCreateError] = useState<string | null>(null);
  const [closeImpact, setCloseImpact] = useState<{
    season: Season;
    carryCoins: number;
    expireCoins: number;
    users: number;
  } | null>(null);
  const [closing, setClosing] = useState(false);
  const [carryIntoID, setCarryIntoID] = useState<string>("");
  const [closeError, setCloseError] = useState<string | null>(null);

  const draftSeasons = useMemo(
    () => state.seasons.filter((s) => s.status === "draft"),
    [state.seasons]
  );

  useEffect(() => {
    void refresh();
  }, []);

  async function refresh() {
    setState((previous) => ({ ...previous, status: "loading", errorMessage: null }));
    try {
      const seasons = await apiClient.listSeasons({ limit: 50 });
      setState({ status: "ready", seasons: seasons.seasons, errorMessage: null });
    } catch (caught) {
      setState({ status: "error", seasons: [], errorMessage: messageFor(caught) });
    }
  }

  async function handleCreate(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (createName.trim() === "" || creating) {
      return;
    }
    setCreating(true);
    setCreateError(null);
    try {
      await apiClient.createSeason({ name: createName.trim(), status: "draft" });
      setCreateName("");
      await refresh();
    } catch (caught) {
      setCreateError(messageFor(caught));
    } finally {
      setCreating(false);
    }
  }

  async function activate(season: Season) {
    try {
      await apiClient.activateSeason(season.id);
      await refresh();
    } catch (caught) {
      setState((previous) => ({ ...previous, errorMessage: messageFor(caught) }));
    }
  }

  async function startClose(season: Season) {
    try {
      const entries = await fetchAllLedger(season.id);
      const buckets = fifoBucketsFromLedger(entries);
      let carryCoins = 0;
      let expireCoins = 0;
      const usersTouched = new Set<string>();
      for (const [userID, { carry, fresh }] of buckets.entries()) {
        if (carry > 0 || fresh > 0) {
          usersTouched.add(userID);
        }
        expireCoins += carry;
        carryCoins += fresh;
      }
      setCarryIntoID("");
      setCloseError(null);
      setCloseImpact({ season, carryCoins, expireCoins, users: usersTouched.size });
    } catch (caught) {
      setState((previous) => ({ ...previous, errorMessage: messageFor(caught) }));
    }
  }

  async function confirmClose() {
    if (closeImpact === null || carryIntoID === "") {
      return;
    }
    setClosing(true);
    setCloseError(null);
    try {
      await apiClient.closeSeason(closeImpact.season.id, carryIntoID);
      setCloseImpact(null);
      await refresh();
    } catch (caught) {
      // Stay open and surface the message — the admin can re-pick a draft
      // if the previously selected one was just activated or closed.
      setCloseError(messageFor(caught));
      await refresh();
    } finally {
      setClosing(false);
    }
  }

  return (
    <div className="seasons-view">
      <section className="card">
        <h2>Create draft season</h2>
        <form onSubmit={handleCreate} className="form-row form-row--inline">
          <label>
            Name
            <input
              type="text"
              value={createName}
              onChange={(event) => setCreateName(event.target.value)}
              placeholder="H2 2026"
              required
            />
          </label>
          <button type="submit" className="btn btn--primary" disabled={creating || createName.trim() === ""}>
            {creating ? "Creating…" : "Create draft"}
          </button>
        </form>
        {createError !== null && <p className="form-row__error">{createError}</p>}
      </section>

      <section className="card">
        <h2>Seasons</h2>
        {state.status === "loading" && <p className="loading">Loading…</p>}
        {state.status === "error" && state.errorMessage !== null && (
          <p className="form-row__error">{state.errorMessage}</p>
        )}
        {state.status === "ready" && state.seasons.length === 0 && (
          <p className="muted">No seasons yet — create one above.</p>
        )}
        {state.status === "ready" && state.seasons.length > 0 && (
          <table className="ledger-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>Starts</th>
                <th>Closed</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {state.seasons.map((season) => (
                <tr key={season.id}>
                  <td>{season.name}</td>
                  <td><StatusBadge status={season.status} /></td>
                  <td>{formatDate(season.starts_at)}</td>
                  <td>{formatDate(season.closed_at)}</td>
                  <td className="row-actions">
                    {season.status === "draft" && (
                      <button type="button" className="btn btn--primary btn--sm" onClick={() => void activate(season)}>
                        Activate
                      </button>
                    )}
                    {season.status === "active" && (
                      <button type="button" className="btn btn--ghost btn--sm" onClick={() => void startClose(season)}>
                        Close…
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {closeImpact !== null && (
        <CloseConfirm
          season={closeImpact.season}
          carryCoins={closeImpact.carryCoins}
          expireCoins={closeImpact.expireCoins}
          users={closeImpact.users}
          drafts={draftSeasons}
          carryIntoID={carryIntoID}
          onCarryIntoChange={setCarryIntoID}
          closing={closing}
          errorMessage={closeError}
          onCancel={() => setCloseImpact(null)}
          onConfirm={() => void confirmClose()}
        />
      )}
    </div>
  );
}

function StatusBadge({ status }: { status: Season["status"] }) {
  return <span className={`status-badge status-badge--${status}`}>{status}</span>;
}

function CloseConfirm({
  season,
  carryCoins,
  expireCoins,
  users,
  drafts,
  carryIntoID,
  onCarryIntoChange,
  closing,
  errorMessage,
  onCancel,
  onConfirm
}: {
  season: Season;
  carryCoins: number;
  expireCoins: number;
  users: number;
  drafts: Season[];
  carryIntoID: string;
  onCarryIntoChange: (id: string) => void;
  closing: boolean;
  errorMessage: string | null;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const impactCopy = useMemo(() => {
    if (carryCoins === 0 && expireCoins === 0) {
      return "No outstanding coins. Closing this season is safe.";
    }
    const usersFragment = `across ${users} user${users === 1 ? "" : "s"}`;
    if (expireCoins === 0) {
      return `${formatNumber(carryCoins)} coin${carryCoins === 1 ? "" : "s"} ${usersFragment} will carry into the season you pick.`;
    }
    if (carryCoins === 0) {
      return `${formatNumber(expireCoins)} coin${expireCoins === 1 ? "" : "s"} of prior carryover ${usersFragment} will expire on close.`;
    }
    return `${formatNumber(carryCoins)} coin${carryCoins === 1 ? "" : "s"} will carry into the season you pick; ${formatNumber(expireCoins)} of prior carryover will expire on close (${usersFragment}).`;
  }, [carryCoins, expireCoins, users]);

  const noDrafts = drafts.length === 0;
  const canConfirm = !closing && !noDrafts && carryIntoID !== "";
  const overlayDismiss = useOverlayDismiss(onCancel, closing);

  return (
    <div className="modal-overlay" role="presentation" {...overlayDismiss}>
      <div className="modal" role="dialog" aria-modal="true">
        <header className="modal__header">
          <h2>Close {season.name}?</h2>
          <button type="button" className="modal__close" onClick={onCancel} disabled={closing}>×</button>
        </header>
        <div className="modal__body">
          <p>
            Unspent coins carry into the season you pick below. Coins already carried from a previous
            season expire on close — they only persist for one season.
          </p>
          <p><strong>{impactCopy}</strong></p>
          {noDrafts ? (
            <p className="form-row__error">
              No draft seasons exist. Create a draft above before closing this season.
            </p>
          ) : (
            <label className="form-row">
              Carry coins into
              <select
                value={carryIntoID}
                onChange={(event) => onCarryIntoChange(event.target.value)}
                disabled={closing}
              >
                <option value="">Select a draft season…</option>
                {drafts.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}</option>
                ))}
              </select>
            </label>
          )}
          <p className="muted">Started {formatDateTime(season.starts_at)}.</p>
          {errorMessage !== null && <p className="form-row__error">{errorMessage}</p>}
          <div className="modal__footer">
            <button type="button" className="btn btn--ghost" onClick={onCancel} disabled={closing}>Cancel</button>
            <button type="button" className="btn btn--primary" onClick={onConfirm} disabled={!canConfirm}>
              {closing ? "Closing…" : "Close season"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

interface FifoBucket {
  carry: number; // unspent portion of prior-season carryover — will expire on close.
  fresh: number; // unspent portion of this-season grants/adjustments — will carry forward.
}

// fifoBucketsFromLedger applies the same FIFO formula the backend uses at
// close-time, so the modal's impact preview matches what the server will
// actually write. Per user: spends consume the carryover bucket first.
//   carry_remaining = max(0, C_in - D)
//   fresh_remaining = max(0, C_in + F - D - carry_remaining)
function fifoBucketsFromLedger(entries: LedgerEntry[]): Map<string, FifoBucket> {
  const cIn = new Map<string, number>(); // season_carryover credits (from prior season)
  const fresh = new Map<string, number>(); // coin_grant + manual_adjustment
  const debits = new Map<string, number>(); // gumball_spend (source side)

  for (const entry of entries) {
    if (entry.entry_type === "season_carryover") {
      if (entry.target_user_id !== null) {
        cIn.set(entry.target_user_id, (cIn.get(entry.target_user_id) ?? 0) + entry.amount);
      }
    } else if (entry.entry_type === "coin_grant" || entry.entry_type === "manual_adjustment") {
      if (entry.target_user_id !== null) {
        fresh.set(entry.target_user_id, (fresh.get(entry.target_user_id) ?? 0) + entry.amount);
      }
    } else if (entry.entry_type === "gumball_spend") {
      if (entry.source_user_id !== null) {
        debits.set(entry.source_user_id, (debits.get(entry.source_user_id) ?? 0) + entry.amount);
      }
    }
    // season_expiry entries shouldn't appear in an active season's ledger, so
    // we ignore them here. If they ever do, they belong on the debit side
    // (source_user_id) but their inclusion would imply the close has already
    // run — in which case the modal shouldn't be opening anyway.
  }

  const userIDs = new Set<string>([...cIn.keys(), ...fresh.keys(), ...debits.keys()]);
  const buckets = new Map<string, FifoBucket>();
  for (const userID of userIDs) {
    const c = cIn.get(userID) ?? 0;
    const f = fresh.get(userID) ?? 0;
    const d = debits.get(userID) ?? 0;
    const carryRemaining = Math.max(0, c - d);
    const freshRemaining = Math.max(0, c + f - d - carryRemaining);
    if (carryRemaining > 0 || freshRemaining > 0) {
      buckets.set(userID, { carry: carryRemaining, fresh: freshRemaining });
    }
  }
  return buckets;
}

function messageFor(caught: unknown): string {
  if (caught instanceof ApiError) {
    return caught.message;
  }
  return caught instanceof Error ? caught.message : "Operation failed.";
}
